import type { ModalStateProps } from '@proton/components/components/modalTwo/useModalState'
import MimeIcon from '@proton/components/components/icon/MimeIcon'
import ModalTwo from '@proton/components/components/modalTwo/Modal'
import ModalTwoContent from '@proton/components/components/modalTwo/ModalContent'
import ModalTwoFooter from '@proton/components/components/modalTwo/ModalFooter'
import ModalTwoHeader from '@proton/components/components/modalTwo/ModalHeader'
import { useModalTwoStatic } from '@proton/components/components/modalTwo/useModalTwo'
import { useNotifications } from '@proton/app-context/useNotifications'
import NotificationButton from '@proton/components/containers/notifications/NotificationButton'
import { NOTIFICATION_DEFAULT_EXPIRATION_TIME } from '@proton/app-context/notifications/constants'
import { useConfirmActionModal } from '@proton/components/components/confirmActionModal/ConfirmActionModal'
import { Button } from '@proton/atoms/Button/Button'
import { Input } from '@proton/atoms/Input/Input'
import { Tooltip } from '@proton/atoms/Tooltip/Tooltip'
import { IcClockRotateLeft } from '@proton/icons/icons/IcClockRotateLeft'
import { IcCrossBig } from '@proton/icons/icons/IcCrossBig'
import { IcEye } from '@proton/icons/icons/IcEye'
import type {
  AuthenticatedDocControllerInterface,
  NativeVersionHistory,
  EditorInvoker,
  EditorControllerInterface,
  DocumentState,
  PublicDocumentState,
} from '@proton/docs-core'
import { useCallback, useMemo, useState } from 'react'
import { c } from 'ttag'
import { HistoryTimeline } from './HistoryTimeline'
import { SingleRevisionViewer } from './SingleRevisionViewer'
import { useLoading } from '@proton/hooks/index'
import type { SerializedEditorState } from 'lexical'
import type { DocumentType } from '@proton/docs-shared'
import { useDebugMode } from '~/utils/debug-mode-context'
import { downloadExport } from '@proton/docs-core/lib/UseCase/ExportAndDownload'
import { downloadJSONFile } from '~/utils/download-json-file'
import downloadFile from '@proton/shared/lib/helpers/downloadFile'
import { useIsODTEnabled } from '~/utils/flags'
import { useOpenDocument } from '@proton/docs-shared/lib/Hooks/useOpenDocument'

type RestoreType = 'replace' | 'as-copy'

type HistoryViewerModalContentProps = {
  versionHistory: NativeVersionHistory
  onClose: () => void
  editorController: EditorControllerInterface
  docController: AuthenticatedDocControllerInterface
  documentType: DocumentType
  documentState: DocumentState | PublicDocumentState
}

function HistoryViewerModalContent({
  versionHistory,
  onClose,
  editorController,
  docController,
  documentType,
  documentState,
}: HistoryViewerModalContentProps) {
  const { isDebugMode } = useDebugMode()
  const isODTEnabled = useIsODTEnabled()

  const [batchThreshold, setBatchThreshold] = useState(() => versionHistory.batchThreshold)
  const [selectedBatchIndex, setSelectedBatchIndex] = useState(() => versionHistory.batches.length - 1)
  const [editorInvoker, setEditorInvoker] = useState<EditorInvoker | undefined>()
  const { createNotification, hideNotification } = useNotifications()

  const [confirmModal, showConfirmModal] = useConfirmActionModal()

  const [isRestoring, withRestoring] = useLoading()
  const [isRestoringAsCopy, withRestoringAsCopy] = useLoading()
  const [isExporting, withExporting] = useLoading()

  const isSelectedRevisionCurrentDocument = useMemo(() => {
    return versionHistory.isCurrentBatchIndex(selectedBatchIndex)
  }, [selectedBatchIndex, versionHistory])

  const selectedBatch = useMemo(() => {
    return versionHistory.batches[selectedBatchIndex]
  }, [versionHistory.batches, selectedBatchIndex])

  const selectedYjsState = useMemo(() => {
    return versionHistory.getMergedUpdateForBatchIndex(selectedBatchIndex)
  }, [selectedBatchIndex, versionHistory])

  const selectedBatchTimestamp = useMemo(() => {
    if (!selectedBatch) {
      return
    }
    return versionHistory.getFormattedDateAndTimeForBatch(selectedBatch)
  }, [selectedBatch, versionHistory])

  const [formattedBatchGroups, setFormattedBatchGroups] = useState(() => versionHistory.getFormattedBatchGroups())

  const showSuccessfulUndoNotification = useCallback(() => {
    createNotification({
      type: 'success',
      text: c('Info').t`Undo successful`,
    })
  }, [createNotification])

  const showSuccessfullRestoreNotification = useCallback(
    (state: SerializedEditorState | unknown) => {
      const shortDate = versionHistory.getShortFormattedDateAndTimeForBatch(selectedBatch)
      const notificationId = createNotification({
        type: 'success',
        expiration: NOTIFICATION_DEFAULT_EXPIRATION_TIME * 1.5,
        text: (
          <>
            <span>
              {/*
               * Translator: This is a notification that appears after restoring a version of the document from history.
               * It is followed by a timestamp. Example: Restored version to 12/01/2024, 12:00
               */}
              {c('Info').t`Restored version to`} {` ${shortDate}`}
            </span>
            <NotificationButton
              onClick={() => {
                hideNotification(notificationId)

                if (documentType === 'sheet') {
                  void withRestoring(
                    editorController
                      .restoreRevisionByReplacingSpreadsheetState(state)
                      .then(showSuccessfulUndoNotification),
                  )
                } else {
                  void withRestoring(
                    editorController
                      .restoreRevisionByReplacingLexicalState(state as SerializedEditorState)
                      .then(showSuccessfulUndoNotification),
                  )
                }
              }}
            >{c('Action').t`Undo`}</NotificationButton>
          </>
        ),
      })
    },
    [
      versionHistory,
      selectedBatch,
      createNotification,
      hideNotification,
      documentType,
      editorController,
      withRestoring,
      showSuccessfulUndoNotification,
    ],
  )

  const openDocument = useOpenDocument()

  const onRestore = useCallback(
    async (restoreType: RestoreType) => {
      if (!editorInvoker) {
        return
      }

      if (restoreType === 'replace') {
        return new Promise<void>((resolve) => {
          showConfirmModal({
            title: c('Action').t`Restore this version?`,
            submitText: c('Action').t`Restore`,
            className: 'restore-revision-modal',
            canUndo: true,
            message:
              documentType === 'sheet'
                ? c('sheets_2025:Info')
                    .t`Your current spreadsheet, including all sheets within it, will be replaced with this version.`
                : c('Info')
                    .t`Your current document will be replaced with this version. Comments and suggestions will be removed from the document but will remain accessible in the comment history.`,
            onCancel: resolve,
            onSubmit: async () => {
              if (documentType === 'sheet') {
                const sheetStateBeforeReplacing = await editorController.getLocalSpreadsheetStateJSON()
                const sheetState = await editorInvoker.getLocalSpreadsheetStateJSON()
                if (!sheetStateBeforeReplacing || !sheetState) {
                  resolve()
                  return
                }
                await editorController.restoreRevisionByReplacingSpreadsheetState(sheetState)
                onClose()
                showSuccessfullRestoreNotification(sheetStateBeforeReplacing)
                resolve()
              } else {
                const editorStateBeforeReplacing = await editorController.getEditorJSON()
                const lexicalState = await editorInvoker.getCurrentEditorState()
                if (!lexicalState || !editorStateBeforeReplacing) {
                  resolve()
                  return
                }
                await editorController.restoreRevisionByReplacingLexicalState(lexicalState)
                onClose()
                showSuccessfullRestoreNotification(editorStateBeforeReplacing)
                resolve()
              }
            },
          })
        })
      } else {
        const yjsState = await editorInvoker.getDocumentState()
        if (!yjsState) {
          return
        }
        try {
          const { nodeMeta, documentType } = await docController.restoreRevisionAsCopy(yjsState)
          openDocument(nodeMeta, documentType)
        } catch {
          // [REALTIME-269] Error ignored in legacy implementation, keeping this behaviour
        }
      }
    },
    [
      editorInvoker,
      showConfirmModal,
      documentType,
      editorController,
      onClose,
      showSuccessfullRestoreNotification,
      docController,
      openDocument,
    ],
  )

  if (!versionHistory.batches.length) {
    return (
      <div className="flex-column flex h-full w-full items-center justify-center">
        {c('Info').t`No version history available`}
      </div>
    )
  }

  return (
    <div className="grid h-full grid-cols-[2fr,_minmax(18.75rem,_auto)]">
      {confirmModal}
      {/* Left column */}
      <div className="flex flex-col">
        <div className="flex items-center gap-3 px-3 py-2.5">
          <IcClockRotateLeft size={5} />
          {selectedBatchTimestamp}
          <div className="flex items-center gap-1 rounded-lg bg-[--background-weak] px-2 py-1 text-sm text-[--text-weak]">
            <IcEye />
            {c('Info').t`View only`}
          </div>
        </div>
        <div className="min-h-0 flex-grow overflow-scroll">
          <SingleRevisionViewer
            key={selectedBatchIndex}
            state={selectedYjsState}
            onEditorInvokerRef={setEditorInvoker}
            documentType={documentType}
          />
        </div>
      </div>

      {/* Right column */}
      <div className="flex h-full flex-col justify-between border-l border-[--border-weak] bg-[--optional-background-lowered]">
        {/* Upper content */}
        <div className="grow basis-0 overflow-auto">
          <div className="sticky top-0 flex items-center justify-between gap-2 bg-[--optional-background-lowered] px-3.5 py-1.5">
            <div className="text-bold text-rg">Document History</div>
            <Tooltip title={c('Action').t`Close`}>
              <Button className="shrink-0" icon shape="ghost" onClick={onClose}>
                <IcCrossBig className="modal-close-icon" alt={c('Action').t`Close`} />
              </Button>
            </Tooltip>
          </div>
          <div className="flex flex-col">
            <HistoryTimeline
              versionHistory={versionHistory}
              formattedBatchGroups={formattedBatchGroups}
              selectedBatchIndex={selectedBatchIndex}
              onSelectedBatchIndexChange={setSelectedBatchIndex}
            />

            <div className="flex items-center gap-2 px-5 pb-3 pt-5">
              <MimeIcon name={documentType === 'sheet' ? 'proton-sheet' : 'proton-doc'} size={4} />
              <span>{c('Info').t`The Beginning`}</span>
            </div>
          </div>
        </div>

        {/* Bottom-anchored content */}
        <div className="mt-auto flex flex-col items-stretch justify-end px-5 pb-3 pt-5">
          {isDebugMode && (
            // eslint-disable-next-line jsx-a11y/label-has-associated-control
            <label className="my-2 flex items-center gap-2">
              <div>Batch granularity:</div>
              <Input
                type="number"
                value={batchThreshold}
                onChange={(event) => {
                  let value = parseInt(event.target.value)
                  if (isNaN(value) || value < 1) {
                    value = 1
                  }
                  setBatchThreshold(value)
                  versionHistory.setBatchThreshold(value)
                  setFormattedBatchGroups(versionHistory.getFormattedBatchGroups())
                  setSelectedBatchIndex(versionHistory.batches.length - 1)
                }}
                min={1}
              />
            </label>
          )}
          {selectedBatchIndex >= 0 && !isSelectedRevisionCurrentDocument && (
            <>
              <Button
                className="mb-2 w-full"
                data-testid="restore-revision-by-copy"
                color="norm"
                loading={isRestoringAsCopy}
                onClick={() => {
                  void withRestoringAsCopy(onRestore('as-copy'))
                }}
              >
                {c('Action').t`Make a copy`}
              </Button>
              <Button
                className="w-full"
                data-testid="restore-revision-by-replace"
                loading={isRestoring}
                onClick={() => {
                  void withRestoring(onRestore('replace'))
                }}
              >
                {c('Action').t`Restore this version`}
              </Button>
              {documentType === 'sheet' && (
                <Button
                  className="mt-2 w-full"
                  data-testid="export-revision"
                  loading={isExporting}
                  disabled={!editorInvoker}
                  onClick={() => {
                    if (!editorInvoker) {
                      return
                    }
                    void withExporting(async () => {
                      const data = await editorInvoker.exportData('xlsx')
                      downloadExport(data, documentState.getProperty('documentName'), 'xlsx')
                    })
                  }}
                >
                  {c('Action').t`Export this version (as xlsx)`}
                </Button>
              )}
              {documentType === 'doc' && (
                <>
                  <Button
                    className="mt-2 w-full"
                    data-testid="export-revision"
                    loading={isExporting}
                    disabled={!editorInvoker}
                    onClick={() => {
                      if (!editorInvoker) {
                        return
                      }
                      void withExporting(async () => {
                        const data = await editorInvoker.exportData('docx')
                        downloadExport(data, documentState.getProperty('documentName'), 'docx')
                      })
                    }}
                  >
                    {c('Action').t`Export this version (as docx)`}
                  </Button>
                  {isODTEnabled && (
                    <Button
                      className="mt-2 w-full"
                      data-testid="export-revision-odt"
                      loading={isExporting}
                      disabled={!editorInvoker}
                      onClick={() => {
                        if (!editorInvoker) {
                          return
                        }
                        void withExporting(async () => {
                          const data = await editorInvoker.exportData('odt')
                          downloadExport(data, documentState.getProperty('documentName'), 'odt')
                        })
                      }}
                    >
                      {c('Action').t`Export this version (as odt) (Beta)`}
                    </Button>
                  )}
                  {isDebugMode && (
                    <>
                      <Button
                        className="mt-2 w-full"
                        data-testid="export-revision"
                        loading={isExporting}
                        disabled={!editorInvoker}
                        onClick={() => {
                          if (!editorInvoker) {
                            return
                          }
                          void withExporting(async () => {
                            const data = await editorInvoker.getCurrentEditorState()
                            if (!data) {
                              return
                            }
                            downloadJSONFile(
                              data,
                              `${documentState.getProperty('documentName')}-editor-state-${selectedBatchTimestamp}.json`,
                            )
                          })
                        }}
                      >
                        {c('Action').t`Export editor state for version`}
                      </Button>
                      <Button
                        className="mt-2 w-full"
                        data-testid="export-revision"
                        loading={isExporting}
                        disabled={!editorInvoker}
                        onClick={() => {
                          if (!editorInvoker) {
                            return
                          }
                          void withExporting(async () => {
                            const data = await editorInvoker.getDocumentState()
                            if (!data) {
                              return
                            }
                            downloadFile(
                              new Blob([data]),
                              `${documentState.getProperty('documentName')}-yjs-state-${selectedBatchTimestamp}.bin`,
                            )
                          })
                        }}
                      >
                        {c('Action').t`Export YJS state for version`}
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

type HistoryViewerModalProps = ModalStateProps & {
  versionHistory: NativeVersionHistory | undefined
  editorController: EditorControllerInterface
  docController: AuthenticatedDocControllerInterface
  documentType: DocumentType
  documentState: DocumentState | PublicDocumentState
}

function HistoryViewerModal({
  versionHistory,
  onClose,
  editorController,
  docController,
  documentType,
  documentState,
  ...rest
}: HistoryViewerModalProps) {
  const isVersionHistoryAvailable = !!versionHistory
  return (
    <ModalTwo
      fullscreen={isVersionHistoryAvailable}
      rootClassName={isVersionHistoryAvailable ? 'p-8 pb-0' : ''}
      className="!rounded-t-xl"
      onClose={onClose}
      data-testid="history-modal"
      {...rest}
    >
      {versionHistory ? (
        <HistoryViewerModalContent
          versionHistory={versionHistory}
          onClose={onClose}
          editorController={editorController}
          docController={docController}
          documentType={documentType}
          documentState={documentState}
        />
      ) : (
        <>
          <ModalTwoHeader title="Document history" />
          <ModalTwoContent>{c('Info').t`No version history available`}</ModalTwoContent>
          <ModalTwoFooter />
        </>
      )}
    </ModalTwo>
  )
}

export function useHistoryViewerModal() {
  return useModalTwoStatic(HistoryViewerModal)
}
